import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { Frown, Smile, ArrowLeft } from 'lucide-react';
import { submitMessageFeedback } from '../services/backendService';
import { validateMessageFeedback } from '../services/validationService';
import { useLanguage, useTranslate } from '../lib/i18n';
import { useInfoCardStack } from '../hooks/queries/useInfoCards';
import MarkdownRenderer from './MarkdownRenderer';
import InformationCards from './InfoCardStackList';
import RoboticIcon from './customIcons/RoboticIcon';
import ThinkingIndicator from './ThinkingIndicator';
import InfoCardsSection from './InfoCardsSection';

import FeedbackSection from './FeedbackSection';
import { MessageOptions } from './MessageOptions';

const MAX_CHARS = 500;

interface Props {
  id: string;
  content: string;
  time: number | string; // TODO: Why do we allow strings and numbers?
  options?: string[];
  feedback?: string;
  onOptionClick: (option: string) => void;
  editMessageFeedbackById: (id: string, feedback: string) => void;
  special?: string;
  topic: string | null;
  isError?: boolean;
  showInfoCards: boolean;
  hideInfoCards: boolean;
  onHideInfoCards: () => void;
  infoCardsContext?: string;
  displayHeader?: boolean;
  infoCardStackId?: string;
  isResearchConsentMessage?: boolean;
  author: string;
}

const AssistantMessage = forwardRef<HTMLDivElement, Props>(
  (
    {
      id,
      content,
      time,
      options = [],
      feedback,
      onOptionClick,
      editMessageFeedbackById,
      special,
      topic,
      isError,
      showInfoCards,
      hideInfoCards,
      onHideInfoCards,
      infoCardsContext,
      displayHeader = true,
      infoCardStackId,
      isResearchConsentMessage = false,
      author,
    },
    chatContainerRef
  ) => {
    const t = useTranslate();
    const modalRef = useRef<HTMLDivElement | null>(null);
    const messageContainerRef = useRef<HTMLDivElement | null>(null);
    const feedbackButtonRef = useRef<HTMLButtonElement | null>(null);

    const [showExpandableBox, setShowExpandableBox] = useState(false);
    const [feebackIndex, setFeedbackIndex] = useState(0);
    const [otherFeedback, setOtherFeedback] = useState('');
    const [selectedOption, setSelectedOption] = useState('');
    const [convertedTime, setConvertedTime] = useState('');
    const [likingDisabled, setLikingDisabled] = useState(true);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [clickedOption, setClickedOption] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submittingFeedback, setSubmittingFeedback] = useState<string | null>(null);
    const [isCardExpanded, setIsCardExpanded] = useState(false);
    const [isNearViewportTop, setIsNearViewportTop] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{
      top: number;
      right: number;
    }>({ top: 0, right: 0 });

    const { language } = useLanguage();
    const { data: infoCardStack } = useInfoCardStack(language, infoCardStackId);

    const shouldRenderInfoCards =
      showInfoCards && !hideInfoCards && !isResearchConsentMessage && infoCardStack;

    useEffect(() => {
      if (!time) return;
      // Parse the input date string
      const date = new Date(time);

      // Get hours and minutes in local time
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      // Return the formatted time
      setConvertedTime(`${hours}:${minutes}`);
    }, [time]);

    useEffect(() => {
      // TODO: This is such a purkka-henkari solution, but it works for now
      if (!(special || content === t('common.labels.writing') || id === 'firstMessage')) {
        setLikingDisabled(false);
      }

      if (isError || author === 'SYSTEM') {
        setLikingDisabled(true);
      }
    }, [special, content, id, isError, t]);

    useEffect(() => {
      // Only check position when the expandable box is shown
      if (showExpandableBox && messageContainerRef.current) {
        const rect = messageContainerRef.current.getBoundingClientRect();
        // Force isNearViewportTop to be true for the first message
        const isNearTop = id === 'firstMessage' ? true : rect.top < 200;

        setIsNearViewportTop(isNearTop);
      }
    }, [showExpandableBox, id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const input = e.target.value;
      if (input.length <= MAX_CHARS) {
        setOtherFeedback(input);

        // Validate input
        const validation = validateMessageFeedback({
          thumbs: 'down',
          freeText: input,
        });

        if (!validation.success && validation.error) {
          if (validation.error.includes('||')) {
            const [key, char] = validation.error.split('||');
            setValidationError(t(key || '', { char }));
          } else {
            setValidationError(t(validation.error));
          }
        } else {
          setValidationError(null);
        }
      }
    };

    const handleSubmit = async () => {
      if (otherFeedback.trim()) {
        setIsSubmitting(true);
        try {
          await submitMessageFeedback({
            messageId: id,
            thumbs: 'down',
            freeText: otherFeedback,
          });

          editMessageFeedbackById(id, t('assistantMessage.feedback.other'));
          setShowExpandableBox(false);
        } finally {
          setIsSubmitting(false);
        }
      } else {
        setShowExpandableBox(false);
      }
    };

    const charCount = otherFeedback.length;
    const isMaxed = charCount === MAX_CHARS - 1;

    const sendGoodFeedback = async () => {
      setSubmittingFeedback('good');
      try {
        await submitMessageFeedback({
          messageId: id,
          thumbs: 'up',
          freeText: '',
        });
        editMessageFeedbackById(id, t('assistantMessage.feedback.good'));
        setShowExpandableBox(false);
      } finally {
        setSubmittingFeedback(null);
      }
    };

    const sendBadFeedback = async () => {
      setSubmittingFeedback('bad');
      try {
        await submitMessageFeedback({
          messageId: id,
          thumbs: 'down',
          freeText: t('assistantMessage.feedback.bad'),
        });
        editMessageFeedbackById(id, t('assistantMessage.feedback.bad'));
        setFeedbackIndex(1);
      } finally {
        setSubmittingFeedback(null);
      }
    };

    const refineBadFeedback = async (freeText: string) => {
      setSubmittingFeedback(freeText);
      try {
        await submitMessageFeedback({
          messageId: id,
          thumbs: 'down',
          freeText,
        });
        editMessageFeedbackById(id, freeText);
        setShowExpandableBox(false);
      } finally {
        setSubmittingFeedback(null);
      }
    };

    const selectSomethingElse = async () => {
      setSubmittingFeedback('other');
      try {
        await submitMessageFeedback({
          messageId: id,
          thumbs: 'down',
          freeText: t('assistantMessage.feedback.other2'),
        });
        editMessageFeedbackById(id, content);
        setFeedbackIndex(2);
      } finally {
        setSubmittingFeedback(null);
      }
    };

    const toggleMenu = () => {
      setFeedbackIndex(0);
      const newState = !showExpandableBox;

      // Calculate menu position based on the feedback button
      if (newState && feedbackButtonRef.current) {
        const buttonRect = feedbackButtonRef.current.getBoundingClientRect();
        const spaceAbove = buttonRect.top;

        // Force isNearTop to be true for the first message
        const isNearTop = id === 'firstMessage' ? true : spaceAbove < 200;

        setIsNearViewportTop(isNearTop);

        // Position menu either below or above the button depending on space
        setMenuPosition({
          top: buttonRect.top,
          right: window.innerWidth - buttonRect.right,
        });
      }

      setShowExpandableBox(newState);
    };

    const handleOptionClick = async (option: string) => {
      setClickedOption(option);
      onOptionClick(option);
      await new Promise((resolve) => setTimeout(resolve, 500));
      setSelectedOption(option);
    };

    const feedbackOptions = [
      t('assistantMessage.feedback.good'),
      t('assistantMessage.feedback.badLanguage'),
      t('assistantMessage.feedback.irrelevant'),
      t('assistantMessage.feedback.topicChange'),
      t('assistantMessage.feedback.refusedDiscussion'),
      t('assistantMessage.feedback.inappropriate'),
    ];

    return (
      <div className="relative my-4 text-card-foreground" ref={messageContainerRef}>
        <div className="flex animate-slide-up items-start">
          <div className="shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
              <span className="text-xs">
                <RoboticIcon size={18} />
              </span>
            </div>
          </div>
          <div className="mx-2 grow space-y-1">
            <div className="flex items-center space-x-2 text-xs">
              {content !== t('common.labels.writing') && displayHeader && (
                <>
                  <span>{t('assistantMessage.botName')}</span>
                  <span>{convertedTime}</span>
                </>
              )}
            </div>
            <div className="space-y-2 text-sm">
              {special ? (
                <>
                  {special === 'moreInfo' && topic && infoCardStack && (
                    <>
                      <p>
                        {t('assistantMessage.infoAboutTopic', {
                          topic: t('topics.' + topic.toLowerCase()),
                        })}
                      </p>
                      <InformationCards stacks={[infoCardStack]} />
                    </>
                  )}
                </>
              ) : content === t('common.labels.writing') ? (
                <ThinkingIndicator />
              ) : (
                <>
                  <MarkdownRenderer markdown={content} />

                  {shouldRenderInfoCards && (
                    <InfoCardsSection
                      infoCardsContext={infoCardsContext}
                      topic={topic!}
                      infoCardStack={infoCardStack}
                      isCardExpanded={isCardExpanded}
                      onToggle={() => setIsCardExpanded(!isCardExpanded)}
                      onHideInfoCards={onHideInfoCards}
                      chatContainerRef={chatContainerRef}
                    />
                  )}

                  {options.length > 0 && (
                    <MessageOptions
                      options={options}
                      selectedOption={selectedOption}
                      clickedOption={clickedOption}
                      handleOptionClick={handleOptionClick}
                    />
                  )}

                  <FeedbackSection
                    feedback={feedback}
                    feedbackOptions={feedbackOptions}
                    toggleMenu={toggleMenu}
                  />
                </>
              )}
            </div>
          </div>
          <div className="shrink-0">
            <button
              type="button"
              ref={feedbackButtonRef}
              className={`flex w-auto flex-col items-center justify-center gap-0.5 rounded-full border px-1 py-1 ${likingDisabled ? 'invisible' : ''}`}
              onClick={toggleMenu}
            >
              <Smile className="text-muted-foreground" size={20} />
              <Frown className="text-muted-foreground" size={20} />
            </button>
          </div>
        </div>
        {showExpandableBox && (
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div onClick={toggleMenu} className="fixed inset-0 z-40 bg-black/50" />
            <div
              ref={modalRef}
              className="fixed z-50"
              style={{
                [isNearViewportTop ? 'top' : 'bottom']: isNearViewportTop
                  ? `${menuPosition.top + (feedbackButtonRef.current?.offsetHeight || 0) + 5}px`
                  : `${window.innerHeight - menuPosition.top + 5}px`,
                right: `${menuPosition.right}px`,
                // Add maxHeight to ensure menu doesn't go off screen
                maxHeight: isNearViewportTop ? 'calc(100vh - 100px)' : `${menuPosition.top - 25}px`,
              }}
            >
              <div
                className={`relative w-full cursor-pointer overflow-hidden rounded-lg border bg-popover text-popover-foreground transition-all duration-300 ease-in-out ${
                  feebackIndex ? 'h-auto w-auto' : 'h-auto w-auto'
                }`}
              >
                <div className="relative z-10">
                  {feebackIndex === 0 && (
                    <div className="grid w-40 justify-items-start space-y-4 py-2 pl-4 pr-2">
                      <button
                        type="button"
                        onClick={sendGoodFeedback}
                        disabled={submittingFeedback !== null}
                        className="flex min-h-[24px] items-center space-x-2"
                      >
                        <div className="flex w-[18px] justify-center">
                          {submittingFeedback === 'good' ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-success border-t-transparent" />
                          ) : (
                            <Smile size={18} className="text-success" />
                          )}
                        </div>
                        <span>{t('assistantMessage.feedback.good')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={sendBadFeedback}
                        disabled={submittingFeedback !== null}
                        className="flex min-h-[24px] items-center space-x-2"
                      >
                        <div className="flex w-[18px] justify-center">
                          {submittingFeedback === 'bad' ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-destructive border-t-transparent" />
                          ) : (
                            <Frown size={18} className="text-destructive" />
                          )}
                        </div>
                        <span>{t('assistantMessage.feedback.bad')}</span>
                      </button>
                    </div>
                  )}
                  {feebackIndex === 1 && (
                    <>
                      <div className="border-b grid w-40 justify-items-start space-y-4 border border-x-0 border-t-0 py-2 pl-4 pr-2">
                        <button
                          type="button"
                          onClick={() => setFeedbackIndex(0)}
                          className="flex items-center space-x-2"
                        >
                          <ArrowLeft size={18} />
                          <span>{t('assistantMessage.feedback.bad')}</span>
                        </button>
                      </div>
                      <div className="grid w-40 justify-items-start space-y-2 py-2 pl-4 pr-2">
                        {[
                          t('assistantMessage.feedback.badLanguage'),
                          t('assistantMessage.feedback.irrelevant'),
                          t('assistantMessage.feedback.topicChange'),
                          t('assistantMessage.feedback.refusedDiscussion'),
                          t('assistantMessage.feedback.inappropriate'),
                        ].map((text) => (
                          <button
                            type="button"
                            key={text}
                            onClick={() => refineBadFeedback(text)}
                            disabled={submittingFeedback !== null}
                            className="relative flex min-h-[24px] w-full items-center py-2 text-left"
                          >
                            {submittingFeedback === text ? (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                              </div>
                            ) : (
                              <span>{text}</span>
                            )}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={selectSomethingElse}
                          disabled={submittingFeedback === 'other'}
                          className="relative flex min-h-[24px] w-full items-center py-2 text-left"
                        >
                          {submittingFeedback === 'other' ? (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                            </div>
                          ) : (
                            <span>{t('assistantMessage.feedback.other2')}</span>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                  {feebackIndex === 2 && (
                    <div className="border-b grid w-48 max-w-md justify-items-start space-y-4 border border-x-0 border-t-0 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setFeedbackIndex(1)}
                        className="flex items-center space-x-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        <span>{t('assistantMessage.feedback.other2')}</span>
                      </button>
                      <div className="w-full space-y-2">
                        <label
                          htmlFor={`feedback-textarea-${id}`}
                          className="block text-sm text-card-foreground"
                        >
                          {t('assistantMessage.feedback.giveFeedback')}
                        </label>
                        <textarea
                          id={`feedback-textarea-${id}`}
                          value={otherFeedback}
                          onChange={handleChange}
                          aria-describedby={`char-count-${id}`}
                          className={`w-full resize-y rounded border p-2 text-sm ${
                            validationError ? 'border-destructive' : ''
                          } ${validationError ? 'focus:border-destructive' : 'focus:border-secondary'}`}
                          rows={3}
                          maxLength={MAX_CHARS}
                        />
                        {validationError && (
                          <span className="text-xs text-destructive">{validationError}</span>
                        )}
                        <div className="flex w-full items-center justify-between">
                          <div className="flex flex-col">
                            <span
                              id={`char-count-${id}`}
                              role="status"
                              aria-live="polite"
                              className={`text-sm ${isMaxed ? 'text-destructive' : 'text-card-foreground'}`}
                            >
                              {charCount}/{MAX_CHARS}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!!validationError || isSubmitting}
                            className={`flex h-[36px] min-w-[90px] items-center justify-center space-x-2 rounded bg-secondary px-4 text-sm font-bold text-secondary-foreground ${
                              validationError || isSubmitting ? 'cursor-not-allowed' : ''
                            }`}
                          >
                            {isSubmitting ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                            ) : (
                              <span>{t('common.submit')}</span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);

AssistantMessage.displayName = 'AssistantMessage';

export default AssistantMessage;
